import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { api, type Envelope } from '@/lib/apiClient';
import type { OrderDTO, OrderStatus, PageMeta } from '@/types/api';

export interface OrderListParams {
  status?: OrderStatus | OrderStatus[];
  date?: string;
  page?: number;
  limit?: number;
  /** Only orders seated at this dine-in table. */
  tableId?: number;
}

export interface OrderListResult {
  items: OrderDTO[];
  meta: PageMeta | null;
}

async function fetchOrders(params: OrderListParams): Promise<OrderListResult> {
  const search: Record<string, string | number> = {};
  if (params.status) {
    const list = Array.isArray(params.status) ? params.status : [params.status];
    if (list.length > 0) search.status = list.join(',');
  }
  if (params.date) search.date = params.date;
  if (params.tableId) search.tableId = params.tableId;
  if (params.page) search.page = params.page;
  if (params.limit) search.limit = params.limit;

  const res = await api.get<Envelope<OrderDTO[]> & { meta?: PageMeta }>('/api/v1/orders', {
    params: search,
  });
  return { items: res.data.data ?? [], meta: res.data.meta ?? null };
}

export function useOrders(params: OrderListParams, options?: Partial<UseQueryOptions<OrderListResult>>) {
  return useQuery({
    queryKey: ['orders', params],
    queryFn: () => fetchOrders(params),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    ...options,
  });
}
